import {TFunction} from "i18next"
import {Communication} from "types/communication"

export const DEPARTMENT_ROUTING_INFO_QUERY_KEY = "department-chat-routing-info"

/**
 * The routing info is requested only for department rooms and only by staff/admin viewers.
 * Students must never request it.
 */
export const canRequestDepartmentRouting = (params: {
    roomType?: Communication.RoomTypes
    roomId?: number | string | null
    isStaffOrAdmin: boolean
}): boolean =>
    params.isStaffOrAdmin && params.roomType === Communication.RoomTypes.Department && !!Number(params.roomId)

/** With `mode === "all"` (or no subunits) there is no rail: the chat looks exactly as before. */
export const shouldShowDepartmentRoutingRail = (info?: Communication.DepartmentRoutingInfo | null): boolean =>
    info?.mode === "subunits" && (info.subunits?.length ?? 0) > 0

/** "Admissions › Fields, Call center" (subunits grouped by department, departments separated by "; ") */
export const formatHandledBySubunits = (subunits: Communication.DepartmentRoutingSubunit[] = []): string => {
    const groups: Array<{departmentName: string; names: string[]}> = []
    subunits.forEach((subunit) => {
        const group = groups.find((item) => item.departmentName === subunit.departmentName)
        if (group) {
            if (!group.names.includes(subunit.name)) {
                group.names.push(subunit.name)
            }
            return
        }
        groups.push({departmentName: subunit.departmentName, names: [subunit.name]})
    })
    return groups
        .map(({departmentName, names}) =>
            departmentName ? `${departmentName} › ${names.join(", ")}` : names.join(", ")
        )
        .join("; ")
}

/** "A", "A and B", "A, B and C" */
export const joinWithAnd = (items: string[], andWord: string): string => {
    if (items.length <= 1) {
        return items[0] ?? ""
    }
    return `${items.slice(0, -1).join(", ")} ${andWord} ${items[items.length - 1]}`
}

export type DepartmentRoutingRailText = {
    handledByLabel: string
    subunitsLabel: string
    statusLabel: string | null
    notifiedLabel: string
    tooltip: string
    /** The whole rail as one sentence (used for the accessible label and in tests) */
    plainText: string
}

export const buildDepartmentRoutingRailText = (
    info: Communication.DepartmentRoutingInfo | null | undefined,
    t: TFunction
): DepartmentRoutingRailText | null => {
    if (!shouldShowDepartmentRoutingRail(info)) {
        return null
    }
    const subunitNames = Array.from(new Set(info.subunits.map((subunit) => subunit.name)))
    const handledByLabel = t("chat:departmentRouting.handledBy")
    const subunitsLabel = formatHandledBySubunits(info.subunits)
    const statusLabel = info.status?.name ?? null
    const notifiedLabel = t("chat:departmentRouting.onlyMembersNotified", {
        subunits: joinWithAnd(subunitNames, t("chat:departmentRouting.and"))
    })
    const parts = [`${handledByLabel} ${subunitsLabel}`]
    if (statusLabel) {
        parts.push(`${t("chat:departmentRouting.studentStatus")} ${statusLabel}`)
    }
    parts.push(notifiedLabel)

    return {
        handledByLabel,
        subunitsLabel,
        statusLabel,
        notifiedLabel,
        tooltip: t("chat:departmentRouting.tooltip"),
        plainText: parts.join(" · ")
    }
}

/** A new message in the open department room from its student refreshes the rail. */
export const shouldRefreshRoutingOnMessage = (
    message: Pick<Communication.Message, "roomId" | "author">,
    room: {roomId: number | string; studentUserId?: number | null; currentUserId: number}
): boolean => {
    if (Number(message.roomId) !== Number(room.roomId)) {
        return false
    }
    const authorUserId = message.author?.userId
    if (!authorUserId || authorUserId === room.currentUserId) {
        return false
    }
    return room.studentUserId ? authorUserId === room.studentUserId : true
}

/**
 * A core `Profile.StudentStatus.Updated` event concerns the open chat when it is about one of its student's
 * profiles. When the profiles are not known yet, refresh anyway (the call is cheap and cached by chat-api).
 */
export const isStatusUpdateForStudent = (eventProfileId: number | undefined, studentProfileIds: number[]): boolean => {
    if (!studentProfileIds.length) {
        return true
    }
    return !!eventProfileId && studentProfileIds.includes(Number(eventProfileId))
}
